import DOMPurify from "dompurify";

/**
 * Sanitize untrusted HTML as a complete document for a sandboxed iframe.
 *
 * WHOLE_DOCUMENT preserves report-level styles from <head>. The SVG profiles
 * keep inline charts and forest plots while DOMPurify still removes event
 * handlers and the active elements forbidden below.
 */
export const sanitizePreviewHtml = (raw: string): string =>
  DOMPurify.sanitize(raw, {
    WHOLE_DOCUMENT: true,
    USE_PROFILES: { html: true, svg: true, svgFilters: true },
    FORBID_TAGS: [
      "script",
      "iframe",
      "object",
      "embed",
      "form",
      "link",
      "meta",
      "base",
      "foreignObject",
    ],
  });

/**
 * Sanitize SVG markup before binding to an &lt;img&gt; blob URL.
 * Strips script / foreignObject / event handlers while keeping vector graphics.
 */
export const sanitizeSvgMarkup = (raw: string): string =>
  DOMPurify.sanitize(raw, {
    USE_PROFILES: { svg: true, svgFilters: true },
    FORBID_TAGS: ["script", "foreignObject"],
  });

/**
 * If the blob is SVG, return a new blob of DOMPurify-cleaned markup.
 * Raster (and other) blobs are returned unchanged.
 */
export const sanitizeImageBlobForPreview = async (
  blob: Blob,
  filename: string,
): Promise<Blob> => {
  const ext = filename.split(".").pop()?.toLowerCase() ?? "";
  const isSvg =
    ext === "svg" ||
    blob.type === "image/svg+xml" ||
    blob.type === "image/svg+xml;charset=utf-8";
  if (!isSvg) return blob;
  const text = await blob.text();
  const clean = sanitizeSvgMarkup(text);
  return new Blob([clean], { type: "image/svg+xml" });
};
